import React from 'react';
import { useTranslation } from 'react-i18next';
import { IconInvoice, IconSuccess, IconArrowRight } from '../../../icons';

interface WelcomeStepProps {
  onNext: () => void;
}

const WelcomeStep: React.FC<WelcomeStepProps> = ({ onNext }) => {
  const { t } = useTranslation('onboarding');

  return (
    <div className="text-center max-w-2xl mx-auto">
      {/* Animated Icon */}
      <div className="mb-8 relative">
        <div className="w-24 h-24 mx-auto bg-gradient-to-br from-indigo-500 to-[#0C1322] rounded-2xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform duration-300">
          <IconInvoice size={48} className="text-primary" />
        </div>
        <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center shadow-lg">
          <IconSuccess size={20} className="text-primary" />
        </div>
      </div>

      {/* Welcome Text */}
      <h2 className="text-3xl font-bold text-primary mb-4">
        {t('steps.welcome.title')}
      </h2>
      
      <p className="text-lg text-secondary mb-6 leading-relaxed">
        {t('steps.welcome.subtitle')}
      </p>

      {/* Benefits List */}
      <div className="bg-[rgb(var(--surface-raised-rgb)/0.5)] rounded-xl p-6 mb-8 text-left border border-hairline">
        <h3 className="text-lg font-semibold text-accent-ink mb-4">
          {t('steps.welcome.whyImportant')}
        </h3>
        
        <ul className="space-y-4">
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mr-3 mt-0.5">
              <span className="text-[var(--tone-success-fg)] text-sm">✓</span>
            </span>
            <div>
              <span className="text-primary font-medium">{t('steps.welcome.benefit1Title')}</span>
              <p className="text-muted text-sm mt-1">{t('steps.welcome.benefit1Desc')}</p>
            </div>
          </li>
          
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mr-3 mt-0.5">
              <span className="text-[var(--tone-success-fg)] text-sm">✓</span>
            </span>
            <div>
              <span className="text-primary font-medium">{t('steps.welcome.benefit2Title')}</span>
              <p className="text-muted text-sm mt-1">{t('steps.welcome.benefit2Desc')}</p>
            </div>
          </li>
          
          <li className="flex items-start">
            <span className="flex-shrink-0 w-6 h-6 rounded-full bg-green-500/20 flex items-center justify-center mr-3 mt-0.5">
              <span className="text-[var(--tone-success-fg)] text-sm">✓</span>
            </span>
            <div>
              <span className="text-primary font-medium">{t('steps.welcome.benefit3Title')}</span>
              <p className="text-muted text-sm mt-1">{t('steps.welcome.benefit3Desc')}</p>
            </div>
          </li>
        </ul>
      </div>

      {/* Time Estimate */}
      <p className="text-muted text-sm mb-6">
        <span className="inline-flex items-center px-3 py-1 rounded-full bg-[rgb(var(--surface-inset-rgb)/0.5)] text-secondary">
          ⏱️ {t('steps.welcome.timeEstimate')}
        </span>
      </p>

      {/* CTA Button */}
      <button
        onClick={onNext}
        className="group inline-flex items-center px-8 py-4 bg-gradient-to-r from-indigo-600 to-indigo-700 text-[#0C1322] font-semibold rounded-xl shadow-lg hover:shadow-indigo-500/30 transform hover:scale-[1.02] transition-all duration-200"
      >
        {t('steps.welcome.startButton')}
        <IconArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
      </button>
    </div>
  );
};

export default WelcomeStep;

